import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/ui/PageLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ApiKeyList from './components/ApiKeyList';
import CreateApiKeyModal from './components/CreateApiKeyModal';
import ApiKeyDetailsModal from './components/ApiKeyDetailsModal';
import UsageAnalytics from './components/UsageAnalytics';

const ApiKeysManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Load API keys using direct Supabase calls
  const loadApiKeys = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await supabase?.from('api_keys')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });
      
      if (fetchError) {
        throw fetchError;
      }

      setApiKeys(data || []);
    } catch (err) {
      console.error('Error loading API keys:', err);
      if (err?.message?.includes('Failed to fetch')) {
        setError('Cannot connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err?.message || 'Failed to load API keys. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      loadApiKeys();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleCreateApiKey = async (keyData) => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    try {
      // Generate a simple API key (in production, this should be more secure)
      const keyPrefix = keyData?.type === 'production' ? 'lp_prod_' : 'lp_dev_';
      const keyValue = keyPrefix + Math.random()?.toString(36)?.substring(2, 15) + Math.random()?.toString(36)?.substring(2, 15);
      
      const { data, error: createError } = await supabase?.from('api_keys')?.insert([{
          user_id: user?.id,
          name: keyData?.name,
          type: keyData?.type,
          permissions: keyData?.permissions || [],
          key_prefix: keyPrefix,
          key_hash: keyValue, // In production, this should be hashed
          status: 'active',
          rate_limit: keyData?.rate_limit || 1000
        }])?.select()?.single();
      
      if (createError) {
        throw createError;
      }

      // Show the new key to user (only time they'll see it)
      setSelectedKey({...data, key_hash: keyValue});
      setShowCreateModal(false);
      setShowNewKeyModal(true);
      await loadApiKeys();
    } catch (err) {
      console.error('Error creating API key:', err);
      if (err?.message?.includes('Failed to fetch')) {
        setError('Cannot connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err?.message || 'Failed to create API key. Please try again.');
      }
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase?.from('api_keys')?.delete()?.eq('id', keyId)?.eq('user_id', user?.id); // Security: only delete user's own keys

      if (deleteError) {
        throw deleteError;
      }

      await loadApiKeys();
    } catch (err) {
      console.error('Error deleting API key:', err);
      if (err?.message?.includes('Failed to fetch')) {
        setError('Cannot connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err?.message || 'Failed to delete API key. Please try again.');
      }
    }
  };

  const handleToggleKey = async (keyId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      const { error: updateError } = await supabase?.from('api_keys')?.update({ 
          status: newStatus,
          updated_at: new Date()?.toISOString()
        })?.eq('id', keyId)?.eq('user_id', user?.id); // Security: only update user's own keys

      if (updateError) {
        throw updateError;
      }

      await loadApiKeys();
    } catch (err) {
      console.error('Error updating API key status:', err);
      if (err?.message?.includes('Failed to fetch')) {
        setError('Cannot connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err?.message || 'Failed to update API key status. Please try again.');
      }
    }
  };

  const handleRevealKey = (keyId) => {
    setRevealedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(keyId)) {
        newSet?.delete(keyId);
      } else {
        newSet?.add(keyId);
      }
      return newSet;
    });
  };

  const handleViewDetails = (apiKey) => {
    setSelectedKey(apiKey);
    setShowDetailsModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text)?.then(() => {
      // Show brief success message
      const originalText = 'Copy to clipboard';
      // You could show a toast notification here
    })?.catch(() => {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body?.appendChild(textArea);
      textArea?.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
      document.body?.removeChild(textArea);
    });
  };

  // Filter API keys based on search query
  const filteredApiKeys = apiKeys?.filter(apiKey =>
    apiKey?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    apiKey?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    apiKey?.permissions?.some(perm => perm?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>API Keys Management - LeadGen Pro</title>
      </Helmet>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">API Keys Management</h1>
          <p className="text-muted-foreground">
            Create and manage API keys for integrating with external services
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateModal(true)}
          >
            Create API Key
          </Button>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      {/* Search */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Search API keys..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="max-w-md"
        />
      </div>
      {/* API Key List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading API keys...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <ApiKeyList
            apiKeys={apiKeys?.filter(k => !searchQuery || k?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()))}
            revealedKeys={revealedKeys}
            onRevealKey={(keyId) => setRevealedKeys(prev => new Set([...prev, keyId]))}
            onHideKey={(keyId) => setRevealedKeys(prev => { const next = new Set(prev); next?.delete(keyId); return next; })}
            onViewDetails={(key) => { setSelectedKey(key); setShowDetailsModal(true); }}
            onDeleteKey={async (keyId) => {
              if (!window.confirm('Are you sure you want to delete this API key?')) return;
              try {
                await supabase?.from('api_keys')?.delete()?.eq('id', keyId);
                await loadApiKeys();
              } catch (err) {
                setError('Failed to delete API key');
              }
            }}
            onToggleStatus={async (keyId, currentStatus) => {
              try {
                await supabase?.from('api_keys')?.update({ status: currentStatus === 'active' ? 'inactive' : 'active' })?.eq('id', keyId);
                await loadApiKeys();
              } catch (err) {
                setError('Failed to update API key status');
              }
            }}
          />
          <UsageAnalytics apiKeys={apiKeys} />
        </div>
      )}
      {/* Modals */}
      {showCreateModal && (
        <CreateApiKeyModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateKey={handleCreateApiKey}
        />
      )}
      {showDetailsModal && selectedKey && (
        <ApiKeyDetailsModal
          isOpen={showDetailsModal}
          apiKey={selectedKey}
          onClose={() => { setShowDetailsModal(false); setSelectedKey(null); }}
          onUpdateKey={async (keyId, updates) => {
            try {
              await supabase?.from('api_keys')?.update(updates)?.eq('id', keyId)?.eq('user_id', user?.id);
              await loadApiKeys();
              setShowDetailsModal(false);
              setSelectedKey(null);
            } catch (err) {
              setError('Failed to update API key');
            }
          }}
          onDeleteKey={async (keyId) => {
            if (!window.confirm('Are you sure you want to delete this API key?')) return;
            try {
              await supabase?.from('api_keys')?.delete()?.eq('id', keyId)?.eq('user_id', user?.id);
              await loadApiKeys();
              setShowDetailsModal(false);
              setSelectedKey(null);
            } catch (err) {
              setError('Failed to delete API key');
            }
          }}
        />
      )}
      {showNewKeyModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 API Key Created!</h3>
            <p className="text-gray-600 mb-4 text-sm">Copy your API key now — it won't be shown again.</p>
            <div className="bg-gray-100 rounded-lg p-3 font-mono text-sm break-all mb-4">{selectedKey?.key_hash}</div>
            <Button variant="default" className="w-full" onClick={() => { setShowNewKeyModal(false); setSelectedKey(null); }}>
              I've Saved My Key
            </Button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ApiKeysManagement;